import React, { useState, useEffect, useRef, useCallback } from 'react';
import { taskAPI, projectAPI, customerAPI, userAPI } from '../services/api';
import { useAuth } from '../AuthContext';
import { contactDisplayName } from '../utils/contactName';
import TaskModal from './TaskModal/TaskModal';
import CustomerModal from './CustomerModal/CustomerModal';
import ProjectModal from './ProjectModal/ProjectModal';
import WorkOrderModal from './WorkOrderModal/WorkOrderModal';
import './TaskDashboard.css';

const TaskDashboard = ({ onNavigateToProject, onNavigateToCustomer, onNavigateToUser, onNavigateToBoards }) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [filterValue, setFilterValue] = useState('incomplete');
    const [openMenuTaskId, setOpenMenuTaskId] = useState(null);
    const { user } = useAuth();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [allProjects, setAllProjects] = useState([]);
    const [allCustomers, setAllCustomers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const searchRef = useRef(null);

    // Add menu ("+ button") state
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [showAddProjectModal, setShowAddProjectModal] = useState(false);
    const [showAddWorkOrderModal, setShowAddWorkOrderModal] = useState(false);
    const addMenuRef = useRef(null);


    const loadSearchData = useCallback(async () => {
        try {
            const [projRes, custRes, userRes] = await Promise.all([
                projectAPI.getAll(),
                // Every contact type, not just homeowners. Restricted to Homeowner this
                // searched 6 of the 553 records on file — the 524 property managers and
                // 23 subcontractors simply were not there, which reads as the search
                // being broken rather than as a filter doing its job.
                customerAPI.getAll({ limit: 1000 }),
                userAPI.getAll(),
            ]);
            setAllProjects(projRes.data);
            setAllCustomers(custRes.data);
            setAllUsers(userRes.data);
        } catch (err) {
            console.error('Error loading search data:', err);
        }
    }, []);

    useEffect(() => {
        fetchTasks();
        loadSearchData();
    }, [loadSearchData]);

    // Close search results on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close add menu on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
                setShowAddMenu(false);
            }
        };
        if (showAddMenu) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAddMenu]);

    const filteredProjects = searchQuery.trim()
        ? allProjects.filter(p =>
            p.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.poNumber && p.poNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (p.customer && `${p.customer.firstName} ${p.customer.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()))
        ).slice(0, 5)
        : [];

    // displayName is the field that matters most here: every one of the 524 property
    // managers has one, but only 454 have a first or last name, so matching on person
    // name alone leaves 70 of them unfindable however they are typed. Digits are
    // compared stripped of formatting, since phones now render as (615) 778-1440 while
    // they are stored bare.
    const customerMatches = (c, q) => {
        const digits = q.replace(/\D/g, '');
        const fields = [c.firstName, c.lastName, c.displayName, c.companyName, c.email];
        if (fields.some(v => v && v.toLowerCase().includes(q))) return true;
        if (!digits) return false;
        return [c.phone, c.mobilePhone, c.mainPhone, c.workPhone]
            .some(v => v && v.replace(/\D/g, '').includes(digits));
    };

    const filteredCustomers = searchQuery.trim()
        ? allCustomers.filter(c => customerMatches(c, searchQuery.trim().toLowerCase())).slice(0, 8)
        : [];

    // Team members are admin-visible only — non-admins shouldn't be poking around colleagues' activity.
    const filteredUsers = (user?.role?.toLowerCase() === 'admin' && searchQuery.trim())
        ? allUsers.filter(u =>
            (u.username && u.username.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()))
        ).slice(0, 5)
        : [];

    const hasResults = filteredProjects.length > 0 || filteredCustomers.length > 0 || filteredUsers.length > 0;

    const handleSearchSelect = useCallback((type, id) => {
        setSearchQuery('');
        setShowSearchResults(false);
        if (type === 'project' && onNavigateToProject) onNavigateToProject(id);
        if (type === 'customer' && onNavigateToCustomer) onNavigateToCustomer(id);
        if (type === 'user' && onNavigateToUser) onNavigateToUser(id);
    }, [onNavigateToProject, onNavigateToCustomer, onNavigateToUser]);

    const fetchTasks = async () => {
        try {
            const response = await taskAPI.getAll();
            const data = response.data;
            if (!Array.isArray(data)) {
                console.error('Tasks endpoint returned non-array payload:', data);
                setTasks([]);
            } else {
                setTasks(data);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            setTasks([]);
            setLoading(false);
        }
    };

    const updateTaskStatus = async (taskId, newStatus) => {
        try {
            const task = tasks.find(t => t.id === taskId);
            const updatedTask = {
                title: task.title,
                description: task.description,
                priority: task.priority,
                status: newStatus,
                assignedToId: task.assignedToId,
                customerId: task.customerId,
                projectId: task.projectId,
                dueDate: task.dueDate,
                completedAt: newStatus === 2 ? new Date().toISOString() : null
            };

            await taskAPI.update(taskId, updatedTask);
            fetchTasks();
        } catch (error) {
            console.error('Error updating task:', error);
            alert('Failed to update task.');
        }
    };

    const handleEdit = (task) => {
        setEditingTask(task);
        setShowEditModal(true);
    };

    const handleDelete = async (taskId) => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            try {
                await taskAPI.delete(taskId);
                fetchTasks();
            } catch (error) {
                console.error('Error deleting task:', error);
                alert('Failed to delete task. You may not have permission.');
            }
        }
    };

    const canEditTask = (task) => {
        return user?.role?.toLowerCase() === 'admin' || task.assignedToId === user?.id;
    };

    const canDeleteTask = (task) => {
        return user?.role?.toLowerCase() === 'admin' || task.assignedToId === user?.id;
    };

    const getPriorityText = (priority) => {
        const priorities = ['Low', 'Medium', 'High', 'Urgent'];
        return priorities[priority] || 'Unknown';
    };

    const getPriorityClass = (priority) => {
        const classes = ['priority-low', 'priority-medium', 'priority-high', 'priority-urgent'];
        return classes[priority] || 'priority-medium';
    };

    const isOverdue = (task) => {
        if (!task.dueDate || task.status === 2) return false;
        return new Date(task.dueDate) < new Date();
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return '';
        const d = new Date(dueDate);
        return d.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleMenuToggle = (taskId) => {
        setOpenMenuTaskId(prev => (prev === taskId ? null : taskId));
    };

    const closeMenu = () => setOpenMenuTaskId(null);

    // Tasks assigned to the current user. Filter chip counts derive from this.
    const myTasks = (Array.isArray(tasks) ? tasks : []).filter(t => t.assignedToId === user?.id);

    const taskCounts = {
        incomplete: myTasks.filter(t => t.status !== 2 && t.status !== 3).length,
        completed: myTasks.filter(t => t.status === 2).length,
        cancelled: myTasks.filter(t => t.status === 3).length,
        all: myTasks.length,
    };

    // Filter tasks to current user, then by status dropdown
    const displayedTasks = myTasks.filter(task => {
        if (filterValue === 'incomplete') return task.status !== 2 && task.status !== 3;
        if (filterValue === 'completed') return task.status === 2;
        if (filterValue === 'cancelled') return task.status === 3;
        return true; // 'all'
    });

    if (loading) {
        return <div className="loading">Loading tasks...</div>;
    }

    return (
        <div className="task-dashboard">
            {/* Header */}
            <div className="dashboard-header">
                <div className="header-left">
                    <h2 className="dashboard-title">My Tasks</h2>
                </div>
                <div className="header-center">
                    <div className="add-menu-wrapper" ref={addMenuRef} style={{ position: 'relative' }}>
                        <button
                            className="btn-add-menu"
                            onClick={() => setShowAddMenu(o => !o)}
                            title="Add new..."
                            style={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '6px',
                                border: '1px solid var(--border-color, #d0d7de)',
                                background: 'var(--card-bg, #fff)',
                                cursor: 'pointer',
                                fontSize: '20px',
                                fontWeight: '500',
                                color: 'var(--text-color, #24292f)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            +
                        </button>
                        {showAddMenu && (
                            <div
                                className="add-menu-dropdown"
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: '6px',
                                    background: 'var(--card-bg, #fff)',
                                    border: '1px solid var(--border-color, #d0d7de)',
                                    borderRadius: '8px',
                                    padding: '6px 0',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    minWidth: '180px',
                                    zIndex: 100,
                                }}
                            >
                                <div style={{ padding: '4px 14px', fontSize: '11px', color: 'var(--text-muted, #57606a)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Add</div>
                                <button type="button" onClick={() => { setShowAddMenu(false); setShowAddCustomerModal(true); }} style={addMenuItemStyle}>
                                    <i className="fas fa-user" style={addMenuIconStyle}></i> Add Customer
                                </button>
                                <button type="button" onClick={() => { setShowAddMenu(false); setShowAddProjectModal(true); }} style={addMenuItemStyle}>
                                    <i className="fas fa-tools" style={addMenuIconStyle}></i> Add Project
                                </button>
                                <button type="button" onClick={() => { setShowAddMenu(false); setShowCreateModal(true); }} style={addMenuItemStyle}>
                                    <i className="fas fa-list" style={addMenuIconStyle}></i> Add Task
                                </button>
                                <button type="button" onClick={() => { setShowAddMenu(false); setShowAddWorkOrderModal(true); }} style={addMenuItemStyle}>
                                    <i className="fas fa-clipboard-list" style={addMenuIconStyle}></i> Add Work Order
                                </button>
                                <button type="button" onClick={() => { setShowAddMenu(false); onNavigateToBoards && onNavigateToBoards(); }} style={addMenuItemStyle}>
                                    <i className="fas fa-columns" style={addMenuIconStyle}></i> Boards
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="dashboard-search" ref={searchRef}>
                        <div className="search-box">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search projects, customers, team members..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setShowSearchResults(true);
                                }}
                                onFocus={() => searchQuery.trim() && setShowSearchResults(true)}
                            />
                            <i className="fas fa-search search-icon"></i>
                        </div>
                        {showSearchResults && searchQuery.trim() && (
                            <div className="search-results-dropdown">
                                {!hasResults ? (
                                    <div className="search-no-results">No results for "{searchQuery}"</div>
                                ) : (
                                    <>
                                        {filteredProjects.length > 0 && (
                                            <div className="search-results-section">
                                                <div className="search-section-header">
                                                    <i className="fas fa-project-diagram"></i> Projects
                                                </div>
                                                {filteredProjects.map(p => (
                                                    <div
                                                        key={`proj-${p.id}`}
                                                        className="search-result-item"
                                                        onClick={() => handleSearchSelect('project', p.id)}
                                                    >
                                                        <div className="search-result-primary">
                                                            {p.projectName}
                                                            {p.poNumber && (
                                                                <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-muted, #57606a)', fontWeight: 'normal' }}>
                                                                    {p.poNumber}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {p.customer && (
                                                            <div className="search-result-secondary">
                                                                {p.customer.firstName} {p.customer.lastName}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {filteredCustomers.length > 0 && (
                                            <div className="search-results-section">
                                                <div className="search-section-header">
                                                    <i className="fas fa-users"></i> Customers
                                                </div>
                                                {filteredCustomers.map(c => (
                                                    <div
                                                        key={`cust-${c.id}`}
                                                        className="search-result-item"
                                                        onClick={() => handleSearchSelect('customer', c.id)}
                                                    >
                                                        <div className="search-result-primary">
                                                            {/* Not firstName + lastName: a company or complex
                                                                has neither, and rendered raw those rows came
                                                                out as a blank line you could click. */}
                                                            {contactDisplayName(c)}
                                                        </div>
                                                        <div className="search-result-secondary">
                                                            {[c.email, c.companyName].filter(Boolean).join(' \u00b7 ')}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {filteredUsers.length > 0 && (
                                            <div className="search-results-section">
                                                <div className="search-section-header">
                                                    <i className="fas fa-user-shield"></i> Team Members
                                                </div>
                                                {filteredUsers.map(u => (
                                                    <div
                                                        key={`user-${u.id}`}
                                                        className="search-result-item"
                                                        onClick={() => handleSearchSelect('user', u.id)}
                                                    >
                                                        <div className="search-result-primary">{u.username}</div>
                                                        <div className="search-result-secondary">
                                                            {u.email}
                                                            {u.role && ` \u00b7 ${u.role}`}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="task-header-right">
                    <select
                        className="task-filter-select"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                    >
                        <option value="incomplete">Incomplete ({taskCounts.incomplete})</option>
                        <option value="completed">Completed ({taskCounts.completed})</option>
                        <option value="cancelled">Cancelled ({taskCounts.cancelled})</option>
                        <option value="all">All Tasks ({taskCounts.all})</option>
                    </select>
                    <button
                        className="btn-create-task"
                        onClick={() => setShowCreateModal(true)}
                    >
                        + Add task
                    </button>
                </div>
            </div>

            {/* Task List */}
            {displayedTasks.length === 0 ? (
                <div className="empty-state">
                    <h3>No tasks found</h3>
                    <p>
                        {filterValue === 'incomplete'
                            ? 'All caught up! Create a new task to get started.'
                            : 'No tasks match this filter.'}
                    </p>
                </div>
            ) : (
                <div className="task-list-table-container">
                    <table className="task-list-table">
                        <colgroup>
                            <col className="col-checkbox" />
                            <col className="col-info" />
                            <col className="col-due" />
                            <col className="col-menu" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>Complete</th>
                                <th>Task Information</th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedTasks.map(task => (
                                <tr
                                    key={task.id}
                                    className={`task-list-row${task.status === 2 ? ' task-row-completed' : ''}`}
                                >
                                    {/* Checkbox */}
                                    <td className="task-list-checkbox-cell">
                                        <input
                                            type="checkbox"
                                            className="task-complete-checkbox"
                                            checked={task.status === 2}
                                            onChange={() =>
                                                updateTaskStatus(task.id, task.status === 2 ? 0 : 2)
                                            }
                                            disabled={!canEditTask(task)}
                                        />
                                    </td>

                                    {/* Task Information */}
                                    <td className="task-info-cell">
                                        <div className="task-list-title-row">
                                            <span
                                                className="task-list-title-link"
                                                onClick={() => handleEdit(task)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => e.key === 'Enter' && handleEdit(task)}
                                            >
                                                {task.title}
                                            </span>
                                            <span className={`priority-badge ${getPriorityClass(task.priority)}`}>
                                                {getPriorityText(task.priority)}
                                            </span>
                                        </div>

                                        {task.description && (
                                            <div className="task-list-description">{task.description}</div>
                                        )}

                                        <div className="task-list-meta">
                                            {task.assignedTo && (
                                                <div className="task-list-meta-line">
                                                    Assigned To:{' '}
                                                    <span className="task-list-meta-link">
                                                        {task.assignedTo.username}
                                                    </span>
                                                </div>
                                            )}
                                            {task.project && (
                                                <div className="task-list-meta-line">
                                                    Related jobs:{' '}
                                                    <span
                                                        className="task-list-meta-link"
                                                        onClick={() =>
                                                            onNavigateToProject && onNavigateToProject(task.project.id)
                                                        }
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) =>
                                                            e.key === 'Enter' &&
                                                            onNavigateToProject &&
                                                            onNavigateToProject(task.project.id)
                                                        }
                                                    >
                                                        {task.project.projectName}
                                                        {task.project.poNumber && (
                                                            <span style={{ marginLeft: '6px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-muted, #57606a)', fontWeight: 'normal' }}>
                                                                {task.project.poNumber}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            {task.customer && (
                                                <div className="task-list-meta-line">
                                                    Related contacts:{' '}
                                                    <span className="task-list-meta-link">
                                                        {task.customer.firstName} {task.customer.lastName}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    {/* Due Date */}
                                    <td className={`task-list-due-cell${isOverdue(task) ? ' task-list-due-overdue' : ''}`}>
                                        {formatDueDate(task.dueDate)}
                                    </td>

                                    {/* Three-dot Menu */}
                                    <td className="task-list-menu-cell">
                                        {openMenuTaskId === task.id && (
                                            <div
                                                className="task-menu-backdrop"
                                                onClick={closeMenu}
                                            />
                                        )}
                                        <button
                                            className="task-menu-trigger"
                                            onClick={() => handleMenuToggle(task.id)}
                                            aria-label="Task actions"
                                        >
                                            &bull;&bull;&bull;
                                        </button>
                                        {openMenuTaskId === task.id && (
                                            <div className="task-menu-dropdown">
                                                {canEditTask(task) && (
                                                    <button
                                                        className="task-menu-item"
                                                        onClick={() => { closeMenu(); handleEdit(task); }}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {canEditTask(task) && task.status !== 2 && (
                                                    <button
                                                        className="task-menu-item"
                                                        onClick={() => { closeMenu(); updateTaskStatus(task.id, 2); }}
                                                    >
                                                        Complete
                                                    </button>
                                                )}
                                                {canDeleteTask(task) && (
                                                    <button
                                                        className="task-menu-item task-menu-delete"
                                                        onClick={() => { closeMenu(); handleDelete(task.id); }}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create Task Modal */}
            <TaskModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onTaskCreated={fetchTasks}
            />

            {/* Edit Task Modal */}
            <TaskModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingTask(null);
                }}
                onTaskCreated={fetchTasks}
                editingTask={editingTask}
            />

            {/* Add Contact Modal (from + menu) */}
            {showAddCustomerModal && (
                <CustomerModal
                    isOpen={showAddCustomerModal}
                    onClose={() => setShowAddCustomerModal(false)}
                    onCustomerCreated={loadSearchData}
                />
            )}

            {/* Add Job Modal (from + menu) */}
            {showAddProjectModal && (
                <ProjectModal
                    isOpen={showAddProjectModal}
                    onClose={() => setShowAddProjectModal(false)}
                    onProjectCreated={loadSearchData}
                    customers={allCustomers}
                    users={allUsers}
                />
            )}

            {/* Add Work Order Modal (from + menu) */}
            {showAddWorkOrderModal && (
                <WorkOrderModal
                    isOpen={showAddWorkOrderModal}
                    onClose={() => setShowAddWorkOrderModal(false)}
                    onSaved={loadSearchData}
                    projects={allProjects}
                    users={allUsers}
                />
            )}

        </div>
    );
};

const addMenuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '8px 14px',
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--text-color, #24292f)',
};

const addMenuIconStyle = {
    width: '18px',
    marginRight: '10px',
    color: 'var(--text-muted, #57606a)',
};

export default TaskDashboard;
